/**
 * Vue 3 composable for Auth0 authentication
 * Provides reactive authentication state and methods
 */
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import { ARCHITEIA_NAMESPACE, LEGACY_ARCHITEIA_NAMESPACE } from '../types.js';
import { AUTH_INIT_TIMEOUT_MS, createAuthTimeoutError, isAuthInitTimeoutError, withTimeout } from '../utils/timeout.js';
import { DEV_AUTH_BYPASS_SESSION_MS, DEV_AUTH_BYPASS_TOKEN, createDevAuthBypassUser, isDevAuthBypassEnabled } from '../devAuthBypass.js';
function isAuth0DebugEnabled() {
    return import.meta.env?.VITE_AUTH0_DEBUG === 'true';
}
/**
 * Resolve `config.initTimeoutMs`, falling back to `AUTH_INIT_TIMEOUT_MS` for
 * `undefined`, non-finite values (e.g. `NaN`), and negative values.
 *
 * Only `0` is a valid opt-out (mirrors `withTimeout()`'s own `timeoutMs <= 0`
 * escape hatch, a test-only affordance). A negative value is not a
 * meaningful "disable the bound" signal — it's a config typo — but without
 * this guard it would pass straight through and hit that same `<= 0` branch,
 * silently disabling the bound and recreating the original production hang
 * (GitHub Copilot review of PR #50).
 *
 * `NaN` was previously handled inconsistently too: `withTimeout()`'s
 * `timeoutMs <= 0` check is `false` for `NaN`, so `initialize()` fired its
 * timeout almost immediately, while `handleRedirectCallback()`'s own
 * `timeoutMs > 0` deadline check is also `false` for `NaN`, treating it as
 * unbounded — opposite behavior for the same nonsensical value (verification
 * review of the KORA-66 fix pass, follow-up).
 */
function resolveInitTimeoutMs(config) {
    const { initTimeoutMs } = config;
    if (initTimeoutMs === 0)
        return 0;
    if (Number.isFinite(initTimeoutMs) && initTimeoutMs > 0) {
        return initTimeoutMs;
    }
    return AUTH_INIT_TIMEOUT_MS;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
/** Convert an Auth0 SDK rejection into the Error shape exposed by this composable. */
function normalizeAuth0Error(error, fallbackMessage) {
    if (error instanceof Error)
        return error;
    const description = isRecord(error) && typeof error.error_description === 'string'
        ? error.error_description.trim()
        : '';
    const normalizedError = new Error(description || fallbackMessage);
    if (isRecord(error) && typeof error.error === 'string') {
        normalizedError.code = error.error;
    }
    return normalizedError;
}
/** True when the current URL is the Auth0 redirect callback round-trip. */
function isAuthCallbackUrl() {
    if (typeof window === 'undefined')
        return false;
    const { pathname, search } = window.location;
    if (pathname === '/callback')
        return true;
    const params = new URLSearchParams(search);
    return params.has('code') && params.has('state');
}
/**
 * Auth0 error codes that mean "the user is cleanly logged out", not "something failed".
 * Matched against the SDK's structured `error` field only — never against message text,
 * which can legitimately mention these codes in an unrelated failure (MM-331 / AU2).
 */
const EXPECTED_LOGGED_OUT_ERROR_CODES = [
    'login_required',
    'consent_required',
    'missing_refresh_token'
];
/** True for Auth0 silent-auth rejections that mean "logged out", not "error". */
function isExpectedLoggedOutError(err) {
    const code = typeof err === 'object' && err !== null && 'error' in err
        ? String(err.error)
        : '';
    return EXPECTED_LOGGED_OUT_ERROR_CODES.includes(code);
}
/** Remove any legacy @@auth0spajs@@* entries left by the old localstorage default. */
function purgeStaleAuth0LocalStorage() {
    try {
        if (typeof window === 'undefined' || !window.localStorage)
            return;
        const stale = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith('@@auth0spajs@@'))
                stale.push(key);
        }
        stale.forEach(key => window.localStorage.removeItem(key));
    }
    catch {
        // localStorage may be unavailable (SSR, privacy mode); purge is best-effort.
    }
}
/**
 * Auth0 composable
 *
 * @example
 * ```ts
 * const { isAuthenticated, user, login, logout, getAccessToken } = useAuth0({
 *   domain: 'your-tenant.auth0.com',
 *   clientId: 'your-client-id',
 *   audience: 'https://api.pinakes.de'
 * })
 * ```
 */
export function useAuth0(config) {
    // Reactive state
    const isAuthenticated = ref(false);
    const isLoading = ref(true);
    const user = ref(null);
    const error = ref(null);
    const accessToken = ref(null);
    const idToken = ref(null);
    const expiresAt = ref(null);
    // Backing store for `initTimedOut` (computed, below). Deliberately NOT derived
    // from `error` — `error` is a shared, multi-writer slot (getAccessToken/login/
    // logout/loadUser/handleRedirectCallback all write it), so a later unrelated
    // error would silently flip a computed-over-error `initTimedOut` back to
    // `false`, re-enabling the Auth0 auto-login loop A7 exists to prevent. Set
    // exclusively in onInitializeTimeout(); cleared exclusively on that same
    // timeout's late-completion heal (KORA-66 fix pass, should-fix 3).
    const initTimedOutFlag = ref(false);
    // Auth0 client instance
    let auth0Client = null;
    let initializePromise = null;
    /**
     * MM-464: populate the SAME reactive refs useAuth0 already owns with the fixed
     * dev-bypass session. CONVENTIONS.md requires useAuth0 remain the single owner of
     * reactive auth state — this deliberately writes those refs rather than introducing
     * a parallel state object. `auth0Client` is left null: no Auth0 client is ever
     * constructed and no Auth0 network traffic occurs.
     */
    function applyDevAuthBypassSession() {
        isAuthenticated.value = true;
        user.value = createDevAuthBypassUser();
        accessToken.value = DEV_AUTH_BYPASS_TOKEN;
        idToken.value = DEV_AUTH_BYPASS_TOKEN;
        expiresAt.value = Date.now() + DEV_AUTH_BYPASS_SESSION_MS;
        error.value = null;
        initTimedOutFlag.value = false;
        isLoading.value = false;
    }
    /**
     * The body of initialize(), verbatim. Never rejects — all failure paths are
     * normalized onto `error.value` inside the try/catch below. KORA-66: this is
     * wrapped in a hard timeout by initialize() because it can hang forever (the
     * Auth0 SDK's worker bridge has no timeout of its own).
     */
    async function runInitialize() {
        if (isDevAuthBypassEnabled()) {
            applyDevAuthBypassSession();
            return;
        }
        try {
            isLoading.value = true;
            error.value = null;
            // Only purge stale localStorage tokens when the effective cache
            // location is 'memory' — an explicit 'localstorage' opt-in relies on
            // those keys as its actual token cache.
            if ((config.cacheLocation || 'memory') === 'memory') {
                purgeStaleAuth0LocalStorage();
            }
            // Ensure redirectUri always includes /callback
            // If config.redirectUri is set, use it; otherwise use origin + /callback
            const redirectUri = config.redirectUri || window.location.origin + '/callback';
            if (isAuth0DebugEnabled()) {
                // eslint-disable-next-line no-console
                console.log('[Auth0 Client Init]', {
                    'config.redirectUri': config.redirectUri,
                    'calculated redirectUri': redirectUri,
                    'window.location.origin': window.location.origin,
                    'will use': redirectUri,
                    'config object keys': Object.keys(config)
                });
            }
            const clientConfig = {
                domain: config.domain,
                clientId: config.clientId,
                authorizationParams: {
                    redirect_uri: redirectUri,
                    audience: config.audience,
                    scope: config.scope || 'openid profile email'
                },
                cacheLocation: config.cacheLocation || 'memory',
                useRefreshTokens: config.useRefreshTokens ?? true,
                useRefreshTokensFallback: config.useRefreshTokensFallback ?? true
            };
            if (isAuth0DebugEnabled()) {
                // eslint-disable-next-line no-console
                console.log('[Auth0 Client Config]', {
                    ...clientConfig,
                    clientId: clientConfig.clientId.substring(0, 10) + '...' // Partial for security
                });
            }
            auth0Client = await createAuth0Client(clientConfig);
            isAuthenticated.value = await auth0Client.isAuthenticated();
            // With cacheLocation:'memory', a full page reload starts with no in-memory
            // session. Attempt silent restoration via the rotating refresh-token / Auth0
            // SSO iframe flow before deciding the user is logged out. Skip on the redirect
            // callback round-trip — handleRedirectCallback() owns the code exchange there.
            if (!isAuthenticated.value && !isAuthCallbackUrl()) {
                try {
                    await auth0Client.getTokenSilently();
                    isAuthenticated.value = await auth0Client.isAuthenticated();
                }
                catch (silentErr) {
                    if (!isExpectedLoggedOutError(silentErr)) {
                        // eslint-disable-next-line no-console -- unexpected silent-auth failure
                        if (import.meta.env.DEV)
                            console.error('Silent re-auth failed:', silentErr);
                        error.value = normalizeAuth0Error(silentErr, 'Silent re-auth failed');
                    }
                    // login_required / consent_required / missing_refresh_token (exact `error`
                    // code match) => genuinely logged out; not an error.
                }
            }
            if (isAuthenticated.value) {
                await loadUser();
            }
        }
        catch (err) {
            error.value = normalizeAuth0Error(err, 'Failed to initialize Auth0');
            // eslint-disable-next-line no-console -- Debug logging for Auth0 initialization errors
            if (import.meta.env.DEV)
                console.error('Auth0 initialization error:', err);
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Applies fail-open state after the bound on initialize() expires: the user
     * reads as unauthenticated with a typed, structured `auth_init_timeout`
     * error, and initialize() itself still resolves (see initialize() below).
     *
     * A3: does not cancel `pending` — if the underlying SDK call eventually
     * settles, the reactive state heals (unless a newer error has since
     * replaced the timeout error).
     */
    function onInitializeTimeout(pending, timeoutMs) {
        isLoading.value = false;
        isAuthenticated.value = false;
        initTimedOutFlag.value = true;
        const timeoutError = createAuthTimeoutError('initialize', timeoutMs);
        error.value = timeoutError;
        // Unconditional — this is the only signal that separates "logged out" from
        // "auth is broken", and it must survive even in a build where
        // import.meta.env.DEV is not what it looks like (KORA-66 §2). Defensively
        // wrapped so a throwing console.error (e.g. a test harness stubbing it to
        // throw) cannot itself make initialize() reject.
        try {
            // eslint-disable-next-line no-console
            console.error('[auth] auth_init_timeout', {
                timeoutMs,
                cacheLocation: config.cacheLocation ?? 'memory',
                useRefreshTokens: config.useRefreshTokens ?? true,
                hasWorkerSupport: typeof Worker !== 'undefined'
            });
        }
        catch {
            // best-effort diagnostic only; never let logging break the never-rejects contract
        }
        // A3: late completion heals — but never clobber a newer error/state.
        // `pending` (runInitialize()) never rejects, but attach an explicit
        // rejection handler anyway so a late change to that contract cannot
        // produce an unhandled rejection here. `isAuthenticated` itself heals via
        // loadUser()'s own success path (it re-asserts `true` on a valid session),
        // not here — by the time `pending` settles, loadUser() (if it ran) has
        // already set it correctly; this handler only owns `error`/`initTimedOut`.
        pending.then(() => {
            if (error.value === timeoutError)
                error.value = null;
            // Only heal the sticky flag if the late completion actually produced
            // a usable client. `runInitialize()` never rejects — if the hung SDK
            // call eventually fails outright (or resolves into a broken state),
            // its own catch overwrites `error.value` with that failure and
            // leaves `auth0Client` null, but `pending` still fulfills per its
            // never-rejects contract. Clearing the flag in that case would flip
            // `authUnavailable` back to `false` with no usable client behind it,
            // sending the guard down the normal unauthenticated path and
            // recreating the auto-login-loop dead end this flag exists to
            // prevent (verification review of the KORA-66 fix pass, regression 2).
            if (auth0Client)
                initTimedOutFlag.value = false;
        }, () => { });
    }
    /**
     * Initialize Auth0 client.
     *
     * Bounded by `config.initTimeoutMs` (default AUTH_INIT_TIMEOUT_MS). Never
     * rejects — on timeout it resolves with fail-open state instead, because
     * app router guards `await` this with no `try/catch`.
     */
    async function initialize() {
        // If already initializing or initialized, return the existing promise
        if (initializePromise) {
            return initializePromise;
        }
        // If already initialized, don't initialize again
        if (auth0Client) {
            return Promise.resolve();
        }
        const timeoutMs = resolveInitTimeoutMs(config);
        const pending = runInitialize(); // never rejects
        initializePromise = withTimeout(pending, timeoutMs, 'initialize').catch(err => {
            if (isAuthInitTimeoutError(err)) {
                onInitializeTimeout(pending, timeoutMs);
                return;
            }
            // Defensive; unreachable today since runInitialize() never rejects.
            isLoading.value = false;
            isAuthenticated.value = false;
            error.value = normalizeAuth0Error(err, 'Failed to initialize Auth0');
        });
        return initializePromise;
    }
    /**
     * Load current user and token
     */
    async function loadUser() {
        if (!auth0Client) {
            throw new Error('Auth0 client not initialized');
        }
        try {
            const [auth0User, token] = await Promise.all([
                auth0Client.getUser(),
                auth0Client.getTokenSilently()
            ]);
            // A successful token fetch is proof of a valid session. Re-assert here
            // (idempotent when the caller — runInitialize()/handleRedirectCallback() —
            // already set it true) so that a loadUser() call which straddles an
            // initialize() timeout heals `isAuthenticated` too, not just `error`:
            // onInitializeTimeout() force-writes `isAuthenticated.value = false` at
            // the bound, but a returning user whose *profile* fetch (not their auth
            // check) is what hung was already known-authenticated before the timeout
            // fired. Without this, a late-completing loadUser() would populate
            // user/token while isAuthenticated stayed stuck false (KORA-66 fix pass,
            // blocker 1).
            isAuthenticated.value = true;
            if (auth0User) {
                user.value = transformUser(auth0User);
            }
            if (token) {
                accessToken.value = token;
                // Decode token to get expiration
                const tokenData = parseJwt(token);
                const exp = typeof tokenData.exp === 'number' ? tokenData.exp : null;
                expiresAt.value = exp ? exp * 1000 : null;
            }
            // Get ID token
            const idTokenValue = await auth0Client.getIdTokenClaims();
            if (idTokenValue) {
                idToken.value = idTokenValue.__raw;
            }
        }
        catch (err) {
            // Reset authentication state on error
            isAuthenticated.value = false;
            user.value = null;
            accessToken.value = null;
            idToken.value = null;
            expiresAt.value = null;
            error.value = normalizeAuth0Error(err, 'Failed to load user');
            // eslint-disable-next-line no-console -- Debug logging for Auth0 user loading errors
            if (import.meta.env.DEV)
                console.error('Error loading user:', err);
            throw err;
        }
    }
    /**
     * Transform Auth0 SDK user to Auth0User
     */
    function transformUser(auth0SDKUser) {
        const customClaims = extractCustomClaims(auth0SDKUser);
        return {
            sub: auth0SDKUser.sub || '',
            id: customClaims.user_id || auth0SDKUser.sub || '',
            auth0Id: auth0SDKUser.sub || '',
            email: auth0SDKUser.email || '',
            email_verified: auth0SDKUser.email_verified || false,
            name: auth0SDKUser.name || auth0SDKUser.nickname || '',
            nickname: auth0SDKUser.nickname,
            picture: auth0SDKUser.picture,
            avatarUrl: auth0SDKUser.picture,
            updated_at: auth0SDKUser.updated_at,
            // Custom claims
            [ARCHITEIA_NAMESPACE + 'trust_level']: customClaims.trust_level,
            [ARCHITEIA_NAMESPACE + 'community_domain']: customClaims.community_domain,
            [ARCHITEIA_NAMESPACE + 'organizations']: customClaims.organizations || [],
            [ARCHITEIA_NAMESPACE + 'user_id']: customClaims.user_id,
            // Extended properties
            organizations: [], // Will be populated from API
            permissions: [], // Will be populated from API
            createdAt: new Date(),
            updatedAt: new Date()
        };
    }
    /**
     * Extract custom claims from Auth0 user
     */
    function extractCustomClaims(auth0User) {
        const userWithClaims = auth0User;
        // AINFRA-99: new namespace first, legacy name for sessions whose ID token predates the move.
        const read = (name) => userWithClaims[ARCHITEIA_NAMESPACE + name] ??
            userWithClaims[LEGACY_ARCHITEIA_NAMESPACE + name];
        return {
            trust_level: read('trust_level'),
            community_domain: read('community_domain'),
            organizations: read('organizations'),
            user_id: read('user_id')
        };
    }
    /**
     * Parse JWT token
     */
    function parseJwt(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join(''));
            return JSON.parse(jsonPayload);
        }
        catch {
            return {};
        }
    }
    /**
     * Login with redirect
     *
     * @param options.redirectPath - Post-auth in-app route (stored in appState.targetUrl)
     * @param options.screenHint - `signup` opens Auth0 Universal Login on the sign-up screen when enabled for the connection
     * @param options.reauthenticate - Force a fresh sign-in (`prompt=login`) instead of reusing the
     *   existing session, so the access token proves the person authenticated just now (KORA-125
     *   step-up). Never combined with a silent flow.
     */
    async function login(options) {
        if (isDevAuthBypassEnabled()) {
            applyDevAuthBypassSession();
            return;
        }
        if (!auth0Client) {
            await initialize();
        }
        if (!auth0Client) {
            throw new Error('Auth0 client not initialized');
        }
        try {
            // Default post-login route: never send users back to /login (pathname would do that).
            const defaultPath = window.location.pathname === '/login' ? '/' : window.location.pathname;
            const redirectPath = options?.redirectPath ?? defaultPath;
            const screenHint = options?.screenHint;
            const reauthenticate = options?.reauthenticate === true;
            await auth0Client.loginWithRedirect({
                appState: {
                    targetUrl: redirectPath
                },
                ...(screenHint || reauthenticate
                    ? {
                        authorizationParams: {
                            ...(screenHint ? { screen_hint: screenHint } : {}),
                            ...(reauthenticate ? { prompt: 'login' } : {})
                        }
                    }
                    : {})
            });
        }
        catch (err) {
            error.value = normalizeAuth0Error(err, 'Login failed');
            throw err;
        }
    }
    /**
     * Handle redirect callback
     *
     * KORA-66 fix pass, should-fix 5: `initialize()` (called below when
     * `auth0Client` isn't ready yet — the normal /callback case, since the store
     * skips its own eager initialize() on that route), the code exchange, and
     * the subsequent `loadUser()` call all share a single deadline instead of
     * each getting the full `initTimeoutMs` budget. Without this, a
     * slow-but-eventually-successful step could leave `/callback` waiting up
     * to ~3x the configured bound — and `loadUser()` specifically calls
     * `getTokenSilently()` through the same unbounded worker bridge this whole
     * ticket is about, so leaving it uncapped could recreate the original hang
     * on this route even after the exchange itself succeeds in time (GitHub
     * Copilot review of PR #50).
     */
    async function handleRedirectCallback() {
        const timeoutMs = resolveInitTimeoutMs(config);
        // `null` deadline mirrors withTimeout()'s own `timeoutMs <= 0` escape hatch
        // (disabled — tests only).
        const deadline = timeoutMs > 0 ? Date.now() + timeoutMs : null;
        if (!auth0Client) {
            await initialize();
        }
        if (!auth0Client) {
            throw new Error('Auth0 client not initialized');
        }
        // Returns the remaining shared budget, or `null` if the bound is
        // disabled. Throws a timeout error immediately if the budget is already
        // spent, rather than silently granting either step a fresh full budget
        // via withTimeout's `timeoutMs <= 0` passthrough.
        function remainingBudgetOrThrow() {
            if (deadline === null)
                return null;
            const remainingMs = Math.max(0, deadline - Date.now());
            if (remainingMs === 0) {
                throw createAuthTimeoutError('handleRedirectCallback', timeoutMs);
            }
            return remainingMs;
        }
        try {
            const exchangeBudget = remainingBudgetOrThrow();
            const result = exchangeBudget === null
                ? await auth0Client.handleRedirectCallback()
                : await withTimeout(auth0Client.handleRedirectCallback(), exchangeBudget, 'handleRedirectCallback');
            isAuthenticated.value = true;
            const loadUserBudget = remainingBudgetOrThrow();
            if (loadUserBudget === null) {
                await loadUser();
            }
            else {
                await withTimeout(loadUser(), loadUserBudget, 'handleRedirectCallback');
            }
            return result.appState?.targetUrl || '/';
        }
        catch (err) {
            error.value = normalizeAuth0Error(err, 'Redirect callback failed');
            throw err;
        }
    }
    /**
     * Logout
     */
    async function logout(options) {
        if (isDevAuthBypassEnabled()) {
            isAuthenticated.value = false;
            user.value = null;
            accessToken.value = null;
            idToken.value = null;
            expiresAt.value = null;
            error.value = null;
            return;
        }
        if (!auth0Client) {
            return;
        }
        try {
            const returnTo = options?.logoutParams?.returnTo ?? config.logoutRedirectUri ?? window.location.origin;
            await auth0Client.logout({
                logoutParams: {
                    returnTo
                }
            });
            // Clear local state
            isAuthenticated.value = false;
            user.value = null;
            accessToken.value = null;
            idToken.value = null;
            expiresAt.value = null;
        }
        catch (err) {
            error.value = normalizeAuth0Error(err, 'Logout failed');
            throw err;
        }
    }
    /**
     * Get access token (with automatic refresh).
     *
     * `forceRefresh` skips the cache and fetches a new token. A role assigned during a sign-in only
     * shows in the token of the next one, so the API client asks for a fresh token once after
     * `403 INSUFFICIENT_PERMISSIONS` (KORA-171).
     */
    async function getAccessToken(options) {
        if (isDevAuthBypassEnabled()) {
            accessToken.value = DEV_AUTH_BYPASS_TOKEN;
            return DEV_AUTH_BYPASS_TOKEN;
        }
        if (!auth0Client) {
            return null;
        }
        try {
            const token = options?.forceRefresh
                ? await auth0Client.getTokenSilently({ cacheMode: 'off' })
                : await auth0Client.getTokenSilently();
            accessToken.value = token;
            return token;
        }
        catch (err) {
            error.value = normalizeAuth0Error(err, 'Failed to get access token');
            // eslint-disable-next-line no-console -- Debug logging for Auth0 token errors
            if (import.meta.env.DEV)
                console.error('Error getting access token:', err);
            return null;
        }
    }
    /**
     * Get ID token
     */
    async function getIdToken() {
        if (isDevAuthBypassEnabled()) {
            idToken.value = DEV_AUTH_BYPASS_TOKEN;
            return DEV_AUTH_BYPASS_TOKEN;
        }
        if (!auth0Client) {
            return null;
        }
        try {
            const claims = await auth0Client.getIdTokenClaims();
            if (claims) {
                idToken.value = claims.__raw;
                return claims.__raw;
            }
            return null;
        }
        catch (err) {
            error.value = normalizeAuth0Error(err, 'Failed to get ID token');
            return null;
        }
    }
    /**
     * True when the most recent initialize() call gave up after its bound
     * expired, rather than genuinely determining the user is logged out.
     *
     * KORA-66 fix pass, should-fix 3: the publicly-exposed value is still
     * `computed` (CONVENTIONS.md's "derived, not stored" rule), but it wraps a
     * dedicated `initTimedOutFlag` ref rather than being derived from `error` —
     * `error` is a shared multi-writer slot written by getAccessToken/login/
     * logout/loadUser/handleRedirectCallback, so a `computed(() =>
     * isAuthInitTimeoutError(error.value))` could silently flip back to `false`
     * on any later unrelated error, and would also incorrectly read `true` for a
     * `handleRedirectCallback()` timeout (which sets the same error `code` but
     * is not an `initialize()` timeout). `initTimedOutFlag` is set/cleared only
     * by `onInitializeTimeout()`.
     */
    const initTimedOut = computed(() => initTimedOutFlag.value);
    /**
     * Check if token is expired
     */
    const isTokenExpired = computed(() => {
        if (!expiresAt.value)
            return false;
        return Date.now() >= expiresAt.value;
    });
    /**
     * Get token info
     */
    const tokenInfo = computed(() => ({
        accessToken: accessToken.value,
        idToken: idToken.value,
        expiresAt: expiresAt.value,
        scope: null // Could be extracted from token
    }));
    /**
     * Get auth state
     */
    const authState = computed(() => ({
        isAuthenticated: isAuthenticated.value,
        isLoading: isLoading.value,
        user: user.value,
        error: error.value
    }));
    /**
     * Get user's trust level
     */
    const trustLevel = computed(() => {
        if (!user.value)
            return undefined;
        const userWithClaims = user.value;
        return userWithClaims[ARCHITEIA_NAMESPACE + 'trust_level'];
    });
    /**
     * Get user's organizations
     */
    const organizations = computed(() => {
        if (!user.value)
            return [];
        const userWithClaims = user.value;
        return userWithClaims[ARCHITEIA_NAMESPACE + 'organizations'] || [];
    });
    if (getCurrentInstance()) {
        onMounted(() => {
            initialize();
        });
    }
    return {
        // State
        isAuthenticated,
        isLoading,
        user,
        error,
        accessToken,
        idToken,
        expiresAt,
        // Computed
        isTokenExpired,
        tokenInfo,
        authState,
        trustLevel,
        organizations,
        initTimedOut,
        // Methods
        login,
        logout,
        handleRedirectCallback,
        getAccessToken,
        getIdToken,
        loadUser,
        initialize
    };
}
